import * as React from "react";
import { createPortal } from "react-dom";

// ---------------------------------------------------------------------------
// Page header slot
//
// Lets a route render its own header into the app's main header region instead
// of the shared TopBar. The shell renders <PageHeaderSlot /> as the target; a
// page renders <PageHeader>…</PageHeader> and its content is portaled into that
// target. While a page header is mounted, the slot is "active" so the shell can
// hide the default TopBar. Used to give the tablet shell a page-defined header.
// ---------------------------------------------------------------------------

type PageHeaderContextValue = {
  container: HTMLElement | null;
  setContainer: (el: HTMLElement | null) => void;
  active: boolean;
  setActive: (active: boolean) => void;
};

const PageHeaderContext = React.createContext<PageHeaderContextValue | null>(
  null,
);

export function PageHeaderProvider({ children }: { children: React.ReactNode }) {
  const [container, setContainer] = React.useState<HTMLElement | null>(null);
  const [active, setActive] = React.useState(false);

  const value = React.useMemo(
    () => ({ container, setContainer, active, setActive }),
    [container, active],
  );

  return (
    <PageHeaderContext.Provider value={value}>
      {children}
    </PageHeaderContext.Provider>
  );
}

function usePageHeaderContext() {
  const ctx = React.useContext(PageHeaderContext);
  if (!ctx) {
    throw new Error("usePageHeader must be used within a PageHeaderProvider.");
  }
  return ctx;
}

/** True while a page is providing its own header. */
export function usePageHeaderActive() {
  return usePageHeaderContext().active;
}

/** The slot the shell renders; pages portal their header content into it. */
export function PageHeaderSlot({ className }: { className?: string }) {
  const { setContainer } = usePageHeaderContext();
  return <div ref={setContainer} className={className} />;
}

/** Rendered by a route to fill the app's main header region. */
export function PageHeader({ children }: { children: React.ReactNode }) {
  const { container, setActive } = usePageHeaderContext();

  React.useEffect(() => {
    setActive(true);
    return () => setActive(false);
  }, [setActive]);

  return container ? createPortal(children, container) : null;
}
